<template>
  <div>
    <!-- Header -->
    <v-row class="ma-0 my-4 align-center justify-space-between">
      <h1 class="text-h5 font-weight-bold">Mascotas de Apadrinamiento</h1>
      <v-btn v-if="can('shelter-pets:create')" rounded color="primary" depressed @click="openCreateDialog">
        <v-icon left>mdi-plus</v-icon>
        <span class="d-none d-md-inline">Agregar mascota</span>
      </v-btn>
    </v-row>

    <!-- Búsqueda y filtro -->
    <v-row class="ma-0 mb-3" align="center">
      <v-col cols="12" sm="6" md="5" class="pa-0 pr-sm-3">
        <v-text-field
          v-model="search"
          label="Buscar por nombre"
          outlined dense hide-details clearable
          prepend-inner-icon="mdi-magnify"
        />
      </v-col>
      <v-col cols="12" sm="6" md="3" class="pa-0">
        <v-select
          v-model="filterVisible"
          :items="visibleOptions"
          item-text="label"
          item-value="value"
          label="Estado"
          outlined dense hide-details
        />
      </v-col>
    </v-row>

    <!-- Tabla -->
    <v-data-table
      :headers="headers"
      :items="pets"
      :loading="loadingData"
      :options.sync="options"
      :items-per-page="limit"
      :server-items-length="totalItems"
      class="elevation-1"
      :footer-props="{ 'items-per-page-options': [10, 20, 50] }"
    >
      <template v-slot:item.imageUrl="{ item }">
        <v-avatar size="40" class="my-1">
          <v-img :src="item.imageUrl" />
        </v-avatar>
      </template>

      <template v-slot:item.species="{ item }">
        <v-chip
          small
          :color="item.species === 'cat' ? 'green' : 'orange'"
          class="white--text font-weight-bold"
        >
          <v-icon left small>{{ item.species === 'cat' ? 'mdi-cat' : 'mdi-dog' }}</v-icon>
          {{ item.species === 'cat' ? 'Gato' : 'Perro' }}
        </v-chip>
      </template>

      <template v-slot:item.sex="{ item }">
        {{ item.sex === 'female' ? 'Hembra' : 'Macho' }}
      </template>

      <template v-slot:item.birthdate="{ item }">
        {{ item.birthdate | date }}
      </template>

      <template v-slot:item.organizationId="{ item }">
        {{ (organizations.find(o => o.id === item.organizationId) || {}).name || item.organizationId }}
      </template>

      <template v-slot:item.visible="{ item }">
        <v-icon color="green" v-if="item.visible">mdi-check-circle</v-icon>
        <v-icon color="red" v-else>mdi-close-circle</v-icon>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-tooltip v-if="can('shelter-pets:update')" top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              class="mx-1" fab dark depressed x-small color="orange"
              v-bind="attrs" v-on="on"
              @click="openEditDialog(item)"
            >
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
          </template>
          <span>Editar</span>
        </v-tooltip>
        <v-tooltip top v-if="item.visible && can('shelter-pets:delete')">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              class="mx-1" fab dark depressed x-small color="blue-grey"
              v-bind="attrs" v-on="on"
              @click="openDeactivateDialog(item)"
            >
              <v-icon small>mdi-eye-off</v-icon>
            </v-btn>
          </template>
          <span>Desactivar</span>
        </v-tooltip>
        <span v-else style="display: inline-block; width: 28px; margin: 0 4px;" />
      </template>
    </v-data-table>

    <!-- Dialog Crear / Editar -->
    <v-dialog v-model="showDialog" max-width="780" @keydown.esc="closeDialog" @click:outside="closeDialog">
      <v-card>
        <v-card-title class="text-uppercase pb-0">
          {{ editingId ? 'Editar mascota' : 'Nueva mascota' }}
        </v-card-title>
        <hr class="mx-6 my-3" />
        <v-card-text class="pb-0">
          <v-form ref="form" v-model="formValid">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.name"
                  label="Nombre"
                  outlined dense hide-details="auto"
                  :rules="[v => !!v || 'Requerido']"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-menu
                  v-model="birthdateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.birthdate"
                      label="Fecha de nacimiento"
                      outlined dense hide-details="auto"
                      readonly append-icon="mdi-calendar"
                      :rules="[v => !!v || 'Requerido']"
                      v-bind="attrs" v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="form.birthdate"
                    :max="today"
                    locale="es"
                    @input="birthdateMenu = false"
                  />
                </v-menu>
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="form.species"
                  label="Especie"
                  :items="speciesOptions"
                  item-text="label" item-value="value"
                  outlined dense hide-details="auto"
                  :rules="[v => !!v || 'Requerido']"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="form.sex"
                  label="Sexo"
                  :items="sexOptions"
                  item-text="label" item-value="value"
                  outlined dense hide-details="auto"
                  :rules="[v => !!v || 'Requerido']"
                />
              </v-col>

              <v-col cols="12">
                <v-autocomplete
                  v-model="form.organizationId"
                  label="Organización / Fundación"
                  :items="organizations"
                  item-text="name"
                  item-value="id"
                  outlined dense hide-details="auto"
                  :loading="loadingOrganizations"
                  :rules="[v => !!v || 'Requerido']"
                  no-data-text="Sin resultados"
                />
              </v-col>

              <v-col cols="12">
                <v-textarea
                  v-model="form.story"
                  label="Historia de la mascota"
                  outlined dense hide-details="auto"
                  rows="3" auto-grow
                  counter="200"
                  :rules="[v => !!v || 'Requerido', v => (v && v.length <= 200) || 'Máximo 200 caracteres']"
                />
              </v-col>
            </v-row>

            <!-- Imágenes -->
            <v-row class="mt-1">
              <v-col cols="12">
                <p class="subtitle-2 grey--text text--darken-1 mb-2">
                  Imágenes{{ editingId ? ' — dejar vacío para mantener las actuales' : '' }}
                </p>
              </v-col>

              <!-- Foto principal -->
              <v-col cols="12">
                <div class="d-flex align-center">
                  <v-file-input
                    v-model="mainImageFile"
                    accept="image/jpeg"
                    label="Foto principal (JPG, máx 1 MB)"
                    outlined dense hide-details="auto"
                    prepend-icon="" prepend-inner-icon="mdi-camera"
                    :rules="!editingId ? [v => !!v || 'Requerido'] : []"
                    @change="onImageChange('main', $event)"
                  />
                  <v-avatar
                    v-if="mainImagePreview"
                    size="52" class="ml-3 flex-shrink-0"
                    style="border: 2px solid #e0e0e0"
                  >
                    <v-img :src="mainImagePreview" />
                  </v-avatar>
                </div>
                <p v-if="imageErrors.main" class="caption red--text mt-1 mb-0">{{ imageErrors.main }}</p>
              </v-col>

              <!-- Foto adicional 1 -->
              <v-col cols="12" md="6">
                <div class="d-flex align-center">
                  <v-file-input
                    v-model="photo1File"
                    accept="image/jpeg"
                    label="Foto adicional 1 (JPG, máx 1 MB)"
                    outlined dense hide-details="auto"
                    prepend-icon="" prepend-inner-icon="mdi-image"
                    :rules="!editingId ? [v => !!v || 'Requerido'] : []"
                    @change="onImageChange('photo1', $event)"
                  />
                  <v-avatar
                    v-if="photo1Preview"
                    size="52" class="ml-3 flex-shrink-0"
                    style="border: 2px solid #e0e0e0"
                  >
                    <v-img :src="photo1Preview" />
                  </v-avatar>
                </div>
                <p v-if="imageErrors.photo1" class="caption red--text mt-1 mb-0">{{ imageErrors.photo1 }}</p>
              </v-col>

              <!-- Foto adicional 2 -->
              <v-col cols="12" md="6">
                <div class="d-flex align-center">
                  <v-file-input
                    v-model="photo2File"
                    accept="image/jpeg"
                    label="Foto adicional 2 (JPG, máx 1 MB)"
                    outlined dense hide-details="auto"
                    prepend-icon="" prepend-inner-icon="mdi-image"
                    :rules="!editingId ? [v => !!v || 'Requerido'] : []"
                    @change="onImageChange('photo2', $event)"
                  />
                  <v-avatar
                    v-if="photo2Preview"
                    size="52" class="ml-3 flex-shrink-0"
                    style="border: 2px solid #e0e0e0"
                  >
                    <v-img :src="photo2Preview" />
                  </v-avatar>
                </div>
                <p v-if="imageErrors.photo2" class="caption red--text mt-1 mb-0">{{ imageErrors.photo2 }}</p>
              </v-col>
            </v-row>

            <p v-if="formError" class="caption red--text mt-3 mb-0">{{ formError }}</p>
          </v-form>
        </v-card-text>
        <hr class="mx-6 my-3" />
        <v-card-actions class="justify-end px-6 pb-4">
          <v-btn text color="primary" rounded @click="closeDialog">Cancelar</v-btn>
          <v-btn
            v-if="can(editingId ? 'shelter-pets:update' : 'shelter-pets:create')"
            color="primary"
            rounded
            depressed
            :loading="saving"
            @click="submitForm"
          >
            {{ editingId ? 'Guardar cambios' : 'Crear mascota' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog Desactivar -->
    <v-dialog v-model="deactivateDialog" max-width="480" @keydown.esc="deactivateDialog = false">
      <v-card class="pa-2">
        <v-card-title>Desactivar mascota</v-card-title>
        <v-card-text>
          ¿Estás seguro de que deseas desactivar a
          <strong>{{ selectedPet ? selectedPet.name : '' }}</strong>?
          Dejará de ser visible en la plataforma.
        </v-card-text>
        <hr class="mx-4 my-2" />
        <v-card-actions class="justify-end">
          <v-btn text color="primary" rounded @click="deactivateDialog = false">Cancelar</v-btn>
          <v-btn
            v-if="can('shelter-pets:delete')"
            color="blue-grey"
            dark
            rounded
            depressed
            :loading="deactivating"
            @click="confirmDeactivate"
          >
            Sí, desactivar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" bottom right :timeout="4000">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Cerrar</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import ShelterPetsService from '../services/shelter-pets.service';
import OrganizationsService from '../services/organizations.service';
import { can } from '../utils/permissions';

const MAX_SIZE_BYTES = 1048576;

const emptyForm = () => ({
  name: '',
  birthdate: '',
  species: '',
  sex: '',
  organizationId: '',
  story: ''
});

export default {
  name: 'ShelterPets',
  data() {
    return {
      pets: [],
      loadingData: false,
      options: {},
      page: 1,
      limit: 10,
      totalItems: 0,
      headers: [
        { text: '', value: 'imageUrl', sortable: false, width: '60px' },
        { text: 'Nombre', value: 'name', sortable: false },
        { text: 'Especie', value: 'species', sortable: false },
        { text: 'Sexo', value: 'sex', sortable: false },
        { text: 'Nacimiento', value: 'birthdate', sortable: false },
        { text: 'Organización', value: 'organizationId', sortable: false },
        { text: 'Activa', value: 'visible', sortable: false, align: 'center' },
        { text: 'Acciones', value: 'actions', sortable: false, align: 'center' }
      ],
      speciesOptions: [
        { label: 'Gato', value: 'cat' },
        { label: 'Perro', value: 'dog' }
      ],
      sexOptions: [
        { label: 'Hembra', value: 'female' },
        { label: 'Macho', value: 'male' }
      ],

      // Dialog crear/editar
      showDialog: false,
      editingId: null,
      formValid: false,
      saving: false,
      form: emptyForm(),
      formError: '',
      birthdateMenu: false,
      today: new Date().toISOString().substr(0, 10),

      // Imágenes
      mainImageFile: null,
      mainImageBase64: null,
      mainImagePreview: null,
      photo1File: null,
      photo1Base64: null,
      photo1Preview: null,
      photo2File: null,
      photo2Base64: null,
      photo2Preview: null,
      imageErrors: { main: '', photo1: '', photo2: '' },

      // Dialog desactivar
      deactivateDialog: false,
      deactivating: false,
      selectedPet: null,

      search: '',
      filterVisible: null,
      visibleOptions: [
        { label: 'Todas', value: null },
        { label: 'Activas', value: true },
        { label: 'Inactivas', value: false }
      ],

      snackbar: { show: false, text: '', color: 'success' },

      // Organizaciones
      organizations: [],
      loadingOrganizations: false,

      // URLs existentes de fotos adicionales (para edición)
      existingPhoto1Url: null,
      existingPhoto2Url: null
    };
  },
  mounted() {
    this.loadOrganizations();
  },
  watch: {
    options: {
      handler() {
        this.page = this.options.page;
        this.limit = this.options.itemsPerPage;
        this.loadPets();
      },
      deep: true
    },
    search() { this.resetAndReload(); },
    filterVisible() { this.resetAndReload(); }
  },
  methods: {
    can,
    resetAndReload() {
      this.page = 1;
      if (this.options.page !== 1) this.options = { ...this.options, page: 1 };
      else this.loadPets();
    },

    async loadOrganizations() {
      this.loadingOrganizations = true;
      const res = await OrganizationsService.getAll();
      if (res.status === 'ok') {
        this.organizations = res.data;
      }
      this.loadingOrganizations = false;
    },

    async loadPets() {
      this.loadingData = true;
      const res = await ShelterPetsService.getAll(this.page, this.limit, {
        search: this.search || '',
        visible: this.filterVisible
      });
      if (res.status === 'ok') {
        this.pets = res.data.shelterPets;
        this.totalItems = res.data.count;
      }
      this.loadingData = false;
    },

    async openCreateDialog() {
      this.editingId = null;
      this.form = emptyForm();
      this.clearImages();
      this.formError = '';
      this.$nextTick(() => { if (this.$refs.form) this.$refs.form.resetValidation(); });
      this.showDialog = true;
      await this.loadOrganizations();
    },

    async openEditDialog(pet) {
      this.editingId = pet.id;
      this.form = {
        name: pet.name,
        birthdate: pet.birthdate ? pet.birthdate.substr(0, 10) : '',
        species: pet.species,
        sex: pet.sex,
        organizationId: pet.organizationId,
        story: pet.story
      };
      this.existingPhoto1Url = pet.photos && pet.photos[0] ? pet.photos[0].url : null;
      this.existingPhoto2Url = pet.photos && pet.photos[1] ? pet.photos[1].url : null;
      this.clearImages();
      this.formError = '';
      this.$nextTick(() => { if (this.$refs.form) this.$refs.form.resetValidation(); });
      this.showDialog = true;
      await this.loadOrganizations();
    },

    closeDialog() {
      this.showDialog = false;
      this.editingId = null;
      this.form = emptyForm();
      this.existingPhoto1Url = null;
      this.existingPhoto2Url = null;
      this.clearImages();
      this.formError = '';
    },

    clearImages() {
      this.mainImageFile = null;
      this.mainImageBase64 = null;
      this.mainImagePreview = null;
      this.photo1File = null;
      this.photo1Base64 = null;
      this.photo1Preview = null;
      this.photo2File = null;
      this.photo2Base64 = null;
      this.photo2Preview = null;
      this.imageErrors = { main: '', photo1: '', photo2: '' };
    },

    async onImageChange(key, file) {
      this.imageErrors[key] = '';
      this[`${key === 'main' ? 'mainImage' : key}Base64`] = null;
      this[`${key === 'main' ? 'mainImage' : key}Preview`] = null;

      if (!file) return;

      if (file.size > MAX_SIZE_BYTES) {
        this.imageErrors[key] = 'La imagen supera el tamaño máximo permitido de 1 MB';
        if (key === 'main') this.mainImageFile = null;
        else if (key === 'photo1') this.photo1File = null;
        else this.photo2File = null;
        return;
      }

      const { base64, dataUrl } = await this.fileToBase64(file);

      if (key === 'main') {
        this.mainImageBase64 = base64;
        this.mainImagePreview = dataUrl;
      } else if (key === 'photo1') {
        this.photo1Base64 = base64;
        this.photo1Preview = dataUrl;
      } else {
        this.photo2Base64 = base64;
        this.photo2Preview = dataUrl;
      }
    },

    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          resolve({ base64: reader.result.split(',')[1], dataUrl: reader.result });
        };
        reader.onerror = err => reject(err);
      });
    },

    async submitForm() {
      this.formError = '';
      if (!this.$refs.form.validate()) return;
      if (Object.values(this.imageErrors).some(e => !!e)) return;

      if (!this.editingId) {
        if (!this.mainImageBase64 || !this.photo1Base64 || !this.photo2Base64) {
          this.formError = 'Las tres imágenes son requeridas';
          return;
        }
      }

      this.saving = true;

      if (this.editingId) {
        const payload = {
          name: this.form.name,
          birthdate: this.form.birthdate,
          species: this.form.species,
          sex: this.form.sex,
          organizationId: this.form.organizationId,
          story: this.form.story,
          ...(this.mainImageBase64 && { imageUrl: this.mainImageBase64 }),
          ...(this.existingPhoto1Url || this.existingPhoto2Url ? {
            photos: [
              { url: this.photo1Base64 || this.existingPhoto1Url },
              { url: this.photo2Base64 || this.existingPhoto2Url }
            ]
          } : {})
        };
        const res = await ShelterPetsService.update(this.editingId, payload);
        this.handleSaveResponse(res, 'Mascota actualizada correctamente');
      } else {
        const payload = {
          name: this.form.name,
          birthdate: this.form.birthdate,
          species: this.form.species,
          sex: this.form.sex,
          organizationId: this.form.organizationId,
          story: this.form.story,
          imageUrl: this.mainImageBase64,
          photos: [{ url: this.photo1Base64 }, { url: this.photo2Base64 }]
        };
        const res = await ShelterPetsService.create(payload);
        this.handleSaveResponse(res, 'Mascota creada correctamente');
      }

      this.saving = false;
    },

    handleSaveResponse(res, successMessage) {
      if (res.status === 'ok') {
        this.showSnackbar(successMessage, 'success');
        this.closeDialog();
        this.loadPets();
      } else {
        this.formError = res.message || 'Ocurrió un error, intenta nuevamente';
      }
    },

    openDeactivateDialog(pet) {
      this.selectedPet = pet;
      this.deactivateDialog = true;
    },

    async confirmDeactivate() {
      this.deactivating = true;
      const res = await ShelterPetsService.deactivate(this.selectedPet.id);
      if (res.status === 'ok') {
        this.showSnackbar('Mascota desactivada', 'success');
        this.deactivateDialog = false;
        this.selectedPet = null;
        this.loadPets();
      } else {
        this.showSnackbar('Error al desactivar la mascota', 'error');
      }
      this.deactivating = false;
    },

    showSnackbar(text, color) {
      this.snackbar = { show: true, text, color };
    }
  }
};
</script>
